<script setup lang="ts">
import { ref } from 'vue'
import { z } from 'zod'
import BModal from '@/components/common/BModal.vue'
import TextInput from '@/components/common/textInput.vue';
import BButton from '../common/bButton.vue';
import { userApi } from '@/services/api'
import { useUserStore } from '@/config/store/userStore'
import { handlePromiseStatus } from '@/services/application'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const isOpen = defineModel<boolean>('isOpen')
const changingPasswordStatus = ref(null)
const userStore = useUserStore()
const isDev = import.meta.env.MODE === 'development' || window.location.origin.includes('sandbox')
// const isDev = false

const oldPasswordError = ref('')
const oldPassword = ref('')
const newPassword = ref('')
const newPasswordError = ref('')
const confirmNewPassword = ref('')

const passwordSchema = z.string()
  .min(8, t('modules.recruitment.changePassword.minLength'))
  .regex(/[a-z]/, t('modules.recruitment.changePassword.lowercase'))
  .regex(/[A-Z]/, t('modules.recruitment.changePassword.uppercase'))
  .regex(/\d/, t('modules.recruitment.changePassword.digit'))
  .regex(/[!@#$%^&*()\-_=+{};:,<.>/?]/, t('modules.recruitment.changePassword.special'))

const handleSubmit = () => {
  if (!newPassword.value) return;

  if (!userStore.user?.user?.toChangePassword && !oldPassword.value) {
    oldPasswordError.value = t('modules.recruitment.changePassword.enterCurrentPassword')
    return;
  }

  oldPasswordError.value = ''

  if (!isDev) {
    const result = passwordSchema.safeParse(newPassword.value)
    if (!result.success) {
      newPasswordError.value = result.error.issues[0].message
      return;
    }
  }

  if (confirmNewPassword.value !== newPassword.value) {
    newPasswordError.value = t('modules.recruitment.changePassword.confirmationMismatch')
    return;
  }

  newPasswordError.value = ''

  const userId = userStore.user?.user?._id
  if (!userId) {
    console.error('User ID is not defined in user store.')
    return
  }

  const changePasswordRequest = userApi.changeUserPassword(userId, { oldPassword: oldPassword.value, newPassword: newPassword.value })
  handlePromiseStatus(changePasswordRequest.promise, { loadingStatus: changingPasswordStatus })

  changePasswordRequest.promise.then(() => {
    if (userStore.user?.user) {
      userStore.user.user.toChangePassword = false
    }
    isOpen.value = false
    oldPassword.value = ''
    newPassword.value = ''
    confirmNewPassword.value = ''
  }).catch((error) => {
    console.error(error)
    newPasswordError.value = error?.response?.data?.customMessage || error?.response?.data?.message || error?.message || t('common.error')
  })
};

</script>

<template>
  <BModal
    v-model="isOpen"
    position="left"
    data-cy="change-password-modal"
    :disable-back-drop="userStore?.user?.user?.toChangePassword"
    :disable-close="userStore?.user?.user?.toChangePassword"
  >
    <template #header>
      <h2>Change password</h2>
    </template>
    <template #default>
      <form data-cy="change-password-form" @submit.prevent="handleSubmit" class="flex flex-col gap-5 max-w-96">

        <TextInput v-if="!userStore.user?.user?.toChangePassword" label="Current password" input-type="password" id="currentPassword" v-model="oldPassword"
          name="currentPassword" required autocomplete="current-password" placeholder="Your current Password"
          aria-label="Current password" :error-message="oldPasswordError" />

        <div class="flex flex-col gap-2">
          <TextInput input-type="password" id="newPassword" v-model="newPassword" required autocomplete="new-password"
            placeholder="Your new password" aria-label="Your new password" data-cy="change-password-new"
            :error-message="newPasswordError" />
          <p v-if="!isDev" class="text-xs subtitle italic">
            The password must be at least 8 characters, contain at least one uppercase letter, one lowercase
            letter, one number and one special character <br> (ex: <span class="px-2 py-1 text-white bg-fade rounded-sm">!@#$%^&*()-_+=<>?</span>).</p>
        </div>


        <TextInput input-type="password" id="confirmNewPassword" v-model="confirmNewPassword" name="confirmNewPassword"
          required autocomplete="new-password" placeholder="Confirm your new password"
          aria-label="Confirm your new password" data-cy="change-password-confirm" />

        <div class="flex items-center justify-end">
          <BButton type="submit" data-cy="change-password-submit" :loading-status="changingPasswordStatus">
            Change password
          </BButton>
        </div>
      </form>
    </template>
  </BModal>
</template>
